import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Zap, RefreshCw, CheckCircle2, XCircle, Copy, ExternalLink, Send, Search } from "lucide-react";

// Owner-only IndexNow control panel. Shows the site's IndexNow key, lets the
// owner re-verify it's served at the correct URL, ping URLs on demand, and
// see the last N pings (auto-fired on product/blog changes).
export default function SeoPanel() {
  const [state, setState] = useState({ loading: true, data: null, err: null });
  const [manualUrls, setManualUrls] = useState("");
  const [pinging, setPinging] = useState(false);
  const [pingResult, setPingResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    setState((s) => ({ ...s, loading: true, err: null }));
    try {
      const r = await api.get("/admin/seo/indexnow/status", { withCredentials: true });
      setState({ loading: false, data: r.data, err: null });
    } catch (e) {
      setState({ loading: false, data: null, err: e?.response?.data?.detail || "Failed to load status" });
    }
  };

  useEffect(() => { load(); }, []);

  const pingAll = async () => {
    setPinging(true); setPingResult(null);
    try {
      const r = await api.post("/admin/seo/indexnow/ping", { include_all: true }, { withCredentials: true });
      setPingResult(r.data);
    } catch (e) {
      setPingResult({ status: "error", error: e?.response?.data?.detail || "Ping failed" });
    } finally {
      setPinging(false);
      load();
    }
  };

  const pingCustom = async () => {
    const urls = manualUrls.split(/[\n,]/).map((u) => u.trim()).filter(Boolean);
    if (!urls.length) return;
    setPinging(true); setPingResult(null);
    try {
      const r = await api.post("/admin/seo/indexnow/ping", { urls }, { withCredentials: true });
      setPingResult(r.data);
      setManualUrls("");
    } catch (e) {
      setPingResult({ status: "error", error: e?.response?.data?.detail || "Ping failed" });
    } finally {
      setPinging(false);
      load();
    }
  };

  const copyKey = async () => {
    if (!state.data?.key) return;
    try { await navigator.clipboard.writeText(state.data.key); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch {}
  };

  if (state.loading) return <div className="text-white/50 text-sm p-6" data-testid="seo-panel-loading">Loading…</div>;
  if (state.err) return <div className="text-red-400 text-sm p-6" data-testid="seo-panel-error">{state.err}</div>;

  const { key, key_url, site_url, recent } = state.data || {};

  return (
    <section className="mb-12" data-testid="seo-panel">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-display text-2xl text-white flex items-center gap-3">
          <Zap size={22} className="text-[#FF3D8B]" /> Instant Indexing
        </h2>
        <button onClick={load} data-testid="seo-panel-refresh"
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/15 text-white/70 hover:text-white text-xs">
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      <p className="text-white/60 text-sm mb-6 max-w-2xl leading-relaxed">
        IndexNow tells Bing, Yandex, and Seznam the moment a new blog post or
        product goes live — no waiting for the crawler. Auto-fires on every
        product edit and blog publish. You can also trigger a full push here.
      </p>

      {/* Key card */}
      <div className="grid md:grid-cols-2 gap-5 mb-8">
        <div className="rounded-xl border border-white/10 bg-[#0f0f0f] p-5" data-testid="seo-key-card">
          <div className="text-white/50 text-[11px] uppercase tracking-widest mb-2">Site key</div>
          <div className="flex items-center gap-2">
            <code className="text-white text-sm bg-white/5 border border-white/10 rounded px-2 py-1 flex-1 truncate" data-testid="seo-key-value">{key}</code>
            <button onClick={copyKey} data-testid="seo-key-copy"
              className="p-2 rounded-md border border-white/15 text-white/60 hover:text-white hover:border-[#FF3D8B]">
              <Copy size={14} />
            </button>
          </div>
          {copied && <div className="text-[#A855F7] text-xs mt-2">Copied.</div>}
        </div>

        <div className="rounded-xl border border-white/10 bg-[#0f0f0f] p-5" data-testid="seo-verify-card">
          <div className="text-white/50 text-[11px] uppercase tracking-widest mb-2">Verification URL</div>
          <div className="flex items-center gap-2 mb-1">
            <a href={key_url} target="_blank" rel="noreferrer" data-testid="seo-verify-link"
              className="text-[#FF3D8B] text-sm truncate hover:underline flex-1">{key_url}</a>
            <a href={key_url} target="_blank" rel="noreferrer"
              className="p-2 rounded-md border border-white/15 text-white/60 hover:text-white">
              <ExternalLink size={14} />
            </a>
          </div>
          <div className="text-white/40 text-xs">Must return the key as plain text.</div>
        </div>
      </div>

      {/* Ping controls */}
      <div className="rounded-xl border border-white/10 bg-[#0f0f0f] p-5 mb-8">
        <div className="text-white/60 text-[11px] uppercase tracking-widest mb-3">Manual push</div>
        <div className="flex flex-wrap gap-3 items-start">
          <button onClick={pingAll} disabled={pinging} data-testid="seo-ping-all"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FF3D8B] text-black font-bold uppercase tracking-widest text-xs hover:bg-[#ff5ba0] disabled:opacity-50">
            <Send size={14} /> {pinging ? "Pinging…" : "Ping every URL"}
          </button>
          <div className="flex-1 min-w-[280px]">
            <textarea
              value={manualUrls} onChange={(e) => setManualUrls(e.target.value)}
              placeholder="/shop&#10;/product/cherry-bomb&#10;https://bootybreeze.com/blog/…"
              data-testid="seo-ping-urls-input"
              className="w-full h-24 bg-white/5 border border-white/15 rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:border-[#FF3D8B]" />
            <button onClick={pingCustom} disabled={pinging || !manualUrls.trim()} data-testid="seo-ping-custom"
              className="mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/20 text-white/80 hover:border-[#FF3D8B] hover:text-[#FF3D8B] text-xs uppercase tracking-widest disabled:opacity-40">
              Push these URLs
            </button>
          </div>
        </div>
        {pingResult && (
          <div className={`mt-4 text-xs px-3 py-2 rounded-md flex items-start gap-2 ${
            pingResult.status === "ok" ? "bg-emerald-500/10 text-emerald-400" : "bg-red-500/10 text-red-400"
          }`} data-testid="seo-ping-result">
            {pingResult.status === "ok" ? <CheckCircle2 size={14} className="mt-0.5" /> : <XCircle size={14} className="mt-0.5" />}
            <div>
              <div className="font-semibold">
                {pingResult.status === "ok"
                  ? `Accepted (${pingResult.status_code}) — ${pingResult.url_count} URL${pingResult.url_count === 1 ? "" : "s"}`
                  : `Failed — ${pingResult.error || pingResult.status_code || "unknown"}`}
              </div>
              {pingResult.status === "skipped" && <div>{pingResult.reason}</div>}
            </div>
          </div>
        )}
      </div>

      {/* Recent activity */}
      <div className="rounded-xl border border-white/10 bg-[#0f0f0f] overflow-hidden" data-testid="seo-recent-list">
        <div className="px-5 pt-5 pb-3 text-white/60 text-[11px] uppercase tracking-widest">Recent pings</div>
        <table className="w-full text-sm">
          <thead className="text-white/50 text-[11px] uppercase tracking-widest">
            <tr>
              <th className="text-left px-5 py-2">When</th>
              <th className="text-left px-5 py-2">Trigger</th>
              <th className="text-left px-5 py-2">URLs</th>
              <th className="text-left px-5 py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {recent && recent.length ? recent.map((r, idx) => (
              <tr key={r.ts + "-" + idx} className="border-t border-white/5" data-testid={`seo-recent-row-${idx}`}>
                <td className="px-5 py-3 text-white/70 whitespace-nowrap">{new Date(r.ts * 1000).toLocaleString()}</td>
                <td className="px-5 py-3 text-white/70">{r.reason}</td>
                <td className="px-5 py-3 text-white/70">{r.url_count}</td>
                <td className="px-5 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    r.status === "ok" ? "bg-emerald-500/15 text-emerald-400"
                    : r.status === "skipped" ? "bg-white/5 text-white/50"
                    : "bg-red-500/15 text-red-400"
                  }`}>{r.status === "ok" ? `${r.status_code} OK` : r.status}</span>
                </td>
              </tr>
            )) : (
              <tr><td colSpan={4} className="px-5 py-6 text-center text-white/40 text-sm">No pings yet. Publish a blog or edit a product to trigger one.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <GoogleIndexingCard />
    </section>
  );
}

function GoogleIndexingCard() {
  const [state, setState] = useState({ loading: true, data: null });
  const [pinging, setPinging] = useState(false);
  const [result, setResult] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/seo/google/status", { withCredentials: true });
      setState({ loading: false, data: r.data });
    } catch {
      setState({ loading: false, data: { enabled: false, recent: [] } });
    }
  };
  useEffect(() => { load(); }, []);

  const pingAll = async () => {
    setPinging(true); setResult(null);
    try {
      const r = await api.post("/admin/seo/google/ping", { include_all: true }, { withCredentials: true });
      setResult(r.data);
    } catch (e) {
      setResult({ error: e?.response?.data?.detail || "Ping failed" });
    } finally {
      setPinging(false);
      load();
    }
  };

  if (state.loading) return null;
  const { enabled, recent } = state.data || {};

  return (
    <section className="mt-12" data-testid="seo-google-card">
      <h2 className="font-display text-2xl text-white mb-5 flex items-center gap-3">
        <Search size={22} className="text-[#A855F7]" /> Google Indexing API
      </h2>
      {!enabled ? (
        <div className="rounded-xl border border-white/10 bg-[#0f0f0f] p-6 text-white/70 text-sm leading-relaxed" data-testid="seo-google-disabled">
          <div className="text-[#A855F7] text-xs uppercase tracking-widest mb-2">Not configured</div>
          <p className="mb-3">Google's own instant-indexing channel — tells Googlebot to re-crawl within minutes. Free tier: 200 URLs/day.</p>
          <ol className="text-xs text-white/60 space-y-1 list-decimal ml-4">
            <li>Create a Google Cloud project + enable <b>Web Search Indexing API</b>.</li>
            <li>Create a service account and download the JSON key.</li>
            <li>Add the service-account email as an <b>Owner</b> in Google Search Console for bootybreeze.com.</li>
            <li>Base64-encode the JSON and drop it into <code className="text-[#FF3D8B]">GOOGLE_INDEXING_CREDS_B64</code> in the backend <code className="text-[#FF3D8B]">.env</code>.</li>
            <li>Restart the backend.</li>
          </ol>
        </div>
      ) : (
        <>
          <div className="rounded-xl border border-white/10 bg-[#0f0f0f] p-5 mb-4">
            <button onClick={pingAll} disabled={pinging} data-testid="seo-google-ping-all"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#A855F7] text-black font-bold uppercase tracking-widest text-xs hover:bg-[#c084fc] disabled:opacity-50">
              <Send size={14} /> {pinging ? "Publishing…" : "Publish every URL to Google"}
            </button>
            {result && (
              <div className="mt-3 text-xs text-white/70" data-testid="seo-google-result">
                {result.error ? <span className="text-red-400">{result.error}</span>
                : <>Sent — <span className="text-emerald-400">{result.ok} ok</span>, <span className="text-red-400">{result.error} errored</span>{result.rate_limited && <span className="text-yellow-400"> · rate-limited</span>}</>}
              </div>
            )}
          </div>
          <div className="rounded-xl border border-white/10 bg-[#0f0f0f] overflow-hidden" data-testid="seo-google-recent-list">
            <div className="px-5 pt-5 pb-3 text-white/60 text-[11px] uppercase tracking-widest">Recent Google pings</div>
            <table className="w-full text-sm">
              <thead className="text-white/50 text-[11px] uppercase tracking-widest">
                <tr><th className="text-left px-5 py-2">When</th><th className="text-left px-5 py-2">URL</th><th className="text-left px-5 py-2">Type</th><th className="text-left px-5 py-2">Status</th></tr>
              </thead>
              <tbody>
                {recent && recent.length ? recent.map((r, i) => (
                  <tr key={r.ts + "-" + i} className="border-t border-white/5">
                    <td className="px-5 py-3 text-white/70 whitespace-nowrap">{new Date(r.ts * 1000).toLocaleString()}</td>
                    <td className="px-5 py-3 text-white/70 truncate max-w-xs">{r.url}</td>
                    <td className="px-5 py-3 text-white/50 text-xs">{r.kind}</td>
                    <td className="px-5 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        r.status === "ok" ? "bg-emerald-500/15 text-emerald-400"
                        : r.status === "skipped" ? "bg-white/5 text-white/50"
                        : "bg-red-500/15 text-red-400"
                      }`}>{r.status_code || r.status}</span>
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan={4} className="px-5 py-6 text-center text-white/40 text-sm">No Google pings yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
